import React, { useRef, useEffect, useState } from 'react'
import EmojiPicker from './EmojiPicker'

// -- Time formatting (hot path) -----------------------------------------
// Both message lists formatted each row's timestamp INLINE, per message, on
// EVERY render. That call looks cheap but it resolves the locale and builds a
// formatter from scratch every time — fine once, brutal in a loop that re-runs
// constantly. Profiled at rest: 3,861ms of SELF time in a 9.5s capture, ~50%
// of the main thread and the single largest cost in the app.
// Measured here: 400 formats = 54ms inline vs 0ms with a hoisted formatter.
//
// 1. ONE Intl.DateTimeFormat at module scope, reused — identical output, the
//    expensive construction happens once instead of (messages x renders).
// 2. Memoised by timestamp: a message's ts never changes, so its formatted
//    string never changes; after first render each row is a Map lookup.
//    Bounded so a long session can't grow it unbounded (both lists cap at 200
//    and the two panel instances share this cache, so 1000 is ample).
const TIME_FMT = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' })
const timeCache = new Map()
const fmtTime = (ts) => {
  let v = timeCache.get(ts)
  if (v === undefined) {
    v = TIME_FMT.format(new Date(ts))
    if (timeCache.size > 1000) timeCache.clear()   // simple bound; refills lazily
    timeCache.set(ts, v)
  }
  return v
}

// -- Chat bell ----------------------------------------------------------
// Chat messages only — the Log is alerts/DEX/order noise and would chime
// constantly. Three traps this has to dodge, all handled at MODULE scope
// because the panel mounts TWICE (docked grid cell + footer popup) and both
// instances see the same messages:
//   1. Double-ring: chimedRef holds the last chimed id, so whichever
//      instance gets there first rings and the other no-ops.
//   2. History flood: primed=false until the first list is seen, so joining
//      a room with 50 messages doesn't fire 50 bells.
//   3. Own messages: skipped by name match — you know what you just sent.
// WebAudio is created lazily on first ring (autoplay policy needs a prior
// user gesture; joining chat or clicking a tab counts, and if it's still
// blocked the catch keeps it silent instead of throwing).
let bellCtx = null
let lastChimedId = null
let bellPrimed = false
function playChatBell() {
  try {
    if (localStorage.getItem('cheddar_chat_bell') === '0') return
    if (!bellCtx) bellCtx = new (window.AudioContext || window.webkitAudioContext)()
    if (bellCtx.state === 'suspended') bellCtx.resume()
    const t = bellCtx.currentTime
    // two-tone bell: short high strike, softer fifth under it
    for (const [freq, delay, gain] of [[988, 0, 0.16], [1319, 0.055, 0.11]]) {
      const osc = bellCtx.createOscillator()
      const amp = bellCtx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      amp.gain.setValueAtTime(0, t + delay)
      amp.gain.linearRampToValueAtTime(gain, t + delay + 0.008)
      amp.gain.exponentialRampToValueAtTime(0.0001, t + delay + 0.42)
      osc.connect(amp); amp.connect(bellCtx.destination)
      osc.start(t + delay); osc.stop(t + delay + 0.45)
    }
  } catch {}
}

// Base58 mint matcher for turning addresses in chat into clickable chips.
const CHAT_MINT_RE = /[1-9A-HJ-NP-Za-km-z]{32,44}/g


// -- Talk to text (lobby chat composer only) -------------------------
// Docked panel and footer popup both mount MessagesPanel and share one
// chat object. One recognizer at module scope so a click in either view
// starts or stops the same session, and both buttons show listening.
// Web Speech API prompts for the mic itself. Do not call getUserMedia.
// Results only fill the draft (capped at 280). They never send.
const SpeechRecognitionAPI = typeof window !== 'undefined'
  ? (window.webkitSpeechRecognition || window.SpeechRecognition || null)
  : null
let chatRec = null
let chatRecListening = false
const chatRecSubs = new Set()
function setChatRecListening(on) {
  chatRecListening = on
  chatRecSubs.forEach((fn) => fn(on))
}
function stopChatDictation() {
  const rec = chatRec
  if (!rec) {
    setChatRecListening(false)
    return
  }
  try { rec.stop() } catch { setChatRecListening(false) }
}
function endChatDictation(rec) {
  if (chatRec !== rec) return
  chatRec = null
  setChatRecListening(false)
}
function startChatDictation(base, setInput) {
  if (!SpeechRecognitionAPI || typeof setInput !== 'function') return
  if (chatRecListening) {
    stopChatDictation()
    return
  }
  const prefix = String(base || '').replace(/\s+$/, '')
  const rec = new SpeechRecognitionAPI()
  chatRec = rec
  rec.lang = 'en-US'
  rec.continuous = false
  rec.interimResults = true
  rec.onresult = (event) => {
    let spoken = ''
    for (let i = 0; i < event.results.length; i++) {
      spoken += event.results[i][0]?.transcript || ''
    }
    const body = spoken.replace(/^\s+/, '')
    const next = (prefix && body ? prefix + ' ' + body : (prefix || body)).slice(0, 280)
    setInput(next)
  }
  rec.onerror = () => endChatDictation(rec)
  rec.onend = () => endChatDictation(rec)
  try {
    rec.start()
    setChatRecListening(true)
  } catch {
    endChatDictation(rec)
  }
}


/**
 * MessagesPanel — the messages scrollback (Log) + lobby Chat.
 *
 * The *data* is shared (messages log + the chat hook are owned by App, so the
 * docked grid panel and the footer popup show the same content). But each
 * instance owns its own *view state* — which tab is selected and its scroll
 * position — so the docked panel and the popup operate independently: switching
 * tabs or scrolling in one does not move the other.
 *
 * Props:
 *   messages, setMessages            log scrollback array + setter (shared)
 *   messagesOpen, setMessagesOpen    popup visibility (popup instance only)
 *   messagesUnread, setMessagesUnread unread badge count (shared)
 *   chat                             useLobbyChat() return value (shared)
 *   onMintClick(mint)                click handler for mint chips in chat
 *   showToggle                       render the footer toggle button (default true)
 *   docked                           render inline filling a grid cell
 */
export default function MessagesPanel({
  messages,
  setMessages,
  messagesOpen,
  setMessagesOpen,
  messagesUnread,
  setMessagesUnread,
  chatUnread,
  setChatUnread,
  chat,
  onMintClick,
  showToggle = true,
  docked = false,
}) {
  // Per-instance tab + scroll refs so the two views are independent.
  // 'log' | 'chat' | 'ai'. Remembered in localStorage (shared key, so both
  // instances open on the last tab used; switching later stays per-instance).
  const [chatTab, setChatTabState] = useState(() => {
    try { const v = localStorage.getItem('cheddar_chat_tab'); return (v === 'log' || v === 'ai') ? v : 'chat' } catch { return 'chat' }
  })
  const setChatTab = (v) => {
    setChatTabState(v)
    try { localStorage.setItem('cheddar_chat_tab', v) } catch {}
  }
  const [micOn, setMicOn] = useState(chatRecListening)
  useEffect(() => {
    chatRecSubs.add(setMicOn)
    return () => chatRecSubs.delete(setMicOn)
  }, [])
  const [bellOn, setBellOn] = useState(() => {
    try { return localStorage.getItem('cheddar_chat_bell') !== '0' } catch { return true }
  })
  const logBodyRef = useRef(null)
  const chatBodyRef = useRef(null)

  // -- Emoji popup (right-click) ----------------------------------------
  // Composer: inserts at the caret. A chat message: toggles a reaction.
  // Shift+right-click (or right-click with text selected in a message)
  // keeps the browser menu for copy/paste.
  const chatInputRef = useRef(null)
  const [emojiPop, setEmojiPop] = useState(null)   // {x, y, mode:'input'|'react', start, end, id, priv}
  const openInputEmoji = (e) => {
    if (e.shiftKey) return
    e.preventDefault()
    const el = e.currentTarget
    const len = String(chat.input || '').length
    setEmojiPop({ x: e.clientX, y: e.clientY, mode: 'input', start: el.selectionStart ?? len, end: el.selectionEnd ?? len })
  }
  const openReactEmoji = (e, m) => {
    if (e.shiftKey || !chat.joined || typeof chat.react !== 'function' || m.id == null) return
    const sel = window.getSelection?.()
    if (sel && !sel.isCollapsed && String(sel).trim()) return
    e.preventDefault()
    setEmojiPop({ x: e.clientX, y: e.clientY, mode: 'react', id: m.id, priv: !!m.private })
  }
  const pickEmoji = (emoji) => {
    const pop = emojiPop
    setEmojiPop(null)
    if (!pop) return
    if (pop.mode === 'react') { chat.react(pop.id, emoji, pop.priv); return }
    const cur = String(chat.input || '')
    const start = Math.min(pop.start, cur.length), end = Math.min(Math.max(pop.end, start), cur.length)
    const next = cur.slice(0, start) + emoji + cur.slice(end)
    if (next.length > 280) return                   // same cap as the input's maxLength
    chat.setInput(next)
    const caret = start + emoji.length
    requestAnimationFrame(() => {
      const el = chatInputRef.current
      if (!el) return
      el.focus()
      try { el.setSelectionRange(caret, caret) } catch {}
    })
  }
  const closeEmoji = () => {
    const wasInput = emojiPop?.mode === 'input'
    setEmojiPop(null)
    if (wasInput) requestAnimationFrame(() => chatInputRef.current?.focus())
  }
  // Leaving the chat tab (or the composer going away) drops the popup.
  useEffect(() => { setEmojiPop(null) }, [chatTab, chat?.joined])

  // Reaction pills under a message: emoji + count; yours is highlighted.
  // Clicking a pill toggles your reaction with that emoji.
  const renderReactions = (m) => {
    const r = m.reactions
    if (!r || typeof r !== 'object') return null
    const keys = Object.keys(r).filter(k => Array.isArray(r[k]) && r[k].length)
    if (!keys.length) return null
    return (
      <span className="chat-reactions">
        {keys.map(k => {
          const mine = !!chat.name && r[k].includes(chat.name)
          return (
            <button
              key={k}
              type="button"
              className={'chat-react-pill' + (mine ? ' mine' : '')}
              title={r[k].join(', ')}
              disabled={!chat.joined}
              onClick={() => chat.react && chat.react(m.id, k, !!m.private)}
            >{k}<span className="chat-react-count">{r[k].length}</span></button>
          )
        })}
      </span>
    )
  }

  // Autoscroll: pin the active tab's body to the newest message whenever its
  // list grows, the tab changes, OR this instance becomes visible. The
  // visibility term (messagesOpen) is what fixes opening the footer popup
  // while chat is already the selected tab: neither activeCount nor chatTab
  // changes in that case, so without it the popup mounted scrolled to
  // wherever it last sat (mid-history) instead of the newest message.
  // requestAnimationFrame defers the pin until after the popup's body has
  // laid out, so scrollHeight is correct on the same frame it appears.
  const activeCount = chatTab === 'log' ? messages.length : (chat?.messages?.length || 0)
  useEffect(() => {
    const pin = () => {
      const body = chatTab === 'log' ? logBodyRef.current : chatBodyRef.current
      if (!body) return
      // Pin to the NEWEST message, wherever it lives. The log is newest-first
      // (App prepends), so scrollHeight pinned it to the OLDEST row — new
      // entries appeared off-screen at the top, reading as "no autoscroll".
      // Decide direction from the data (ts of first vs last) instead of
      // hardcoding per tab, so a chat-hook ordering change can't re-break it.
      const list = chatTab === 'log' ? messages : (chat?.messages || [])
      const newestFirst = list.length > 1 && list[0].ts >= list[list.length - 1].ts
      body.scrollTop = newestFirst ? 0 : body.scrollHeight
    }
    pin()
    const raf = requestAnimationFrame(pin)
    return () => cancelAnimationFrame(raf)
  // Pin deps are the ARRAYS, not activeCount: both lists cap at 200, so at
  // cap a new message rotates one out and the LENGTH never changes — a length
  // dep stops firing exactly when the list gets busy. The setters always
  // produce a new array reference, so identity catches every update.
  // (activeCount stays for the unread-clear effect below, where per-message
  // granularity at cap doesn't matter.)
  }, [messages, chat?.messages, chatTab, messagesOpen, docked])

  // Clear unread counts ONLY for the footer popup, and ONLY the tab being
  // viewed. The docked panel is always mounted in the workspace, so if it
  // cleared (the old `docked || messagesOpen` rule) it wiped messagesUnread on
  // every new message and the footer badge could never show a count — that was
  // the "no numbers in the footer" regression. So: docked never clears; the
  // open popup clears the active tab's counter (and re-clears when you switch
  // tabs while it's open).
  useEffect(() => {
    if (docked || !messagesOpen) return
    if (chatTab === 'log') setMessagesUnread(0)
    else setChatUnread(0)
  // Deps are the ARRAYS, not activeCount — same reason the pin effect above
  // uses identity. Both lists cap at 200, so once at cap a new message rotates
  // one out and the LENGTH never changes: a length dep stops firing exactly
  // when chat gets busy, and the badge would climb while the panel sat open in
  // front of you. The setters always produce a new array reference, so identity
  // catches every message. (docked still never clears — it is always mounted,
  // and clearing from it wiped the footer badge permanently. That is the
  // "no numbers in the footer" regression; leave that rule alone.)
  }, [docked, messagesOpen, chatTab, messages, chat?.messages, setMessagesUnread, setChatUnread])

  // Ring on genuinely-new chat messages (see module-scope notes above).
  useEffect(() => {
    const list = chat?.messages || []
    if (!list.length) return
    // Newest may be at either end — same ordering logic the pin effect uses.
    const newestFirst = list.length > 1 && list[0].ts >= list[list.length - 1].ts
    const newest = newestFirst ? list[0] : list[list.length - 1]
    if (!newest || newest.id == null) return
    if (!bellPrimed) {            // first sighting = history, never rings
      bellPrimed = true
      lastChimedId = newest.id
      return
    }
    if (newest.id === lastChimedId) return      // already handled (or other instance did)
    lastChimedId = newest.id
    if (chat?.name && newest.name === chat.name) return   // own message
    playChatBell()
  }, [chat?.messages, chat?.name])

  // Render chat text with base58 mint addresses as clickable chips.
  const renderChatText = (text) => {
    const parts = []
    let last = 0, m
    CHAT_MINT_RE.lastIndex = 0
    while ((m = CHAT_MINT_RE.exec(text)) !== null) {
      if (m.index > last) parts.push(text.slice(last, m.index))
      const mint = m[0]
      parts.push(
        <button
          key={`${mint}-${m.index}`}
          className="chat-mint-chip"
          title={mint}
          onClick={() => onMintClick && onMintClick(mint)}
        >{mint.slice(0, 4)}…{mint.slice(-4)}</button>
      )
      last = m.index + mint.length
    }
    if (last < text.length) parts.push(text.slice(last))
    return parts
  }

  // AI tab: only the private Grok thread (your @grok asks + Grok replies).
  const isAi = chatTab === 'ai'
  const chatList = isAi ? (chat?.messages || []).filter(m => m.private) : (chat?.messages || [])
  // Same send path as @grok in Chat: AI tab just adds the prefix.
  const sendNow = () => {
    if (!isAi) return chat.send()
    const t = String(chat.input || '').trim()
    if (!t) return
    chat.send(/^@grok\b/i.test(t) ? t : '@grok ' + t)
  }

  const tabsEl = (
    <div className="messages-panel-tabs">
      <button
        className={`messages-tab ${chatTab === 'log' ? 'active' : ''}`}
        onClick={() => setChatTab('log')}
      >Log</button>
      <button
        className={`messages-tab ${chatTab === 'chat' ? 'active' : ''}`}
        onClick={() => setChatTab('chat')}
      >Chat</button>
      <button
        className={`messages-tab ${chatTab === 'ai' ? 'active' : ''}`}
        title="Talk to Grok - no @grok needed"
        onClick={() => setChatTab('ai')}
      >AI</button>
      <button
        className="messages-tab"
        title={bellOn ? 'Chat sound on' : 'Chat sound off'}
        style={{ marginLeft: 'auto', opacity: bellOn ? 1 : 0.45 }}
        onClick={() => {
          const next = !bellOn
          setBellOn(next)
          try { localStorage.setItem('cheddar_chat_bell', next ? '1' : '0') } catch {}
        }}
      >{bellOn ? '🔔' : '🔕'}</button>
    </div>
  )

  const panelInner = (
    <>
      {/* Docked: tabs live in an ALWAYS-VISIBLE bar, NOT in the hover-reveal
          rail header — the docked panel's .messages-panel-header is registered
          as a hover-reveal panel rail in Workspace, which was hiding the
          Log/Chat tabs until hover. Popup keeps the tabs in its header (that
          instance isn't a rail, works fine). */}
      {docked && (
        <div
          className="messages-tabbar"
          style={{ display: 'flex', flexShrink: 0, gap: 2, padding: '4px 6px', borderBottom: '1px solid var(--seam, #2a2a35)' }}
        >{tabsEl}</div>
      )}
      <div className="messages-panel-header">
        {!docked && tabsEl}
        <div className="messages-panel-actions">
          {chatTab === 'log' && messages.length > 0 && (
            <button
              className="messages-panel-clear"
              title="Clear all"
              onClick={() => { setMessages([]); setMessagesUnread(0) }}
            >Clear</button>
          )}
          {!docked && (
            <button
              className="messages-panel-close"
              title="Close"
              onClick={() => setMessagesOpen(false)}
            >×</button>
          )}
        </div>
      </div>
      {chatTab === 'log' ? (
        <div className="messages-panel-body" ref={logBodyRef}>
          {messages.length === 0 ? (
            <div className="messages-empty">No messages yet</div>
          ) : (
            messages.map(m => (
              <div key={m.id} className={`messages-row messages-${m.kind}`}>
                <span className="messages-row-text">
                  {m.mint && (
                    <button
                      className="messages-row-token"
                      title={m.mint}
                      onClick={() => onMintClick && onMintClick(m.mint)}
                    >{m.name || `${m.mint.slice(0, 4)}…${m.mint.slice(-4)}`}</button>
                  )}
                  {m.mint ? ' ' : ''}{m.text}
                </span>
                <span className="messages-row-time">
                  {fmtTime(m.ts)}
                </span>
              </div>
            ))
          )}
        </div>
      ) : (
        <>
          <div className="messages-panel-body" ref={chatBodyRef}>
            {chatList.length === 0 ? (
              <div className="messages-empty">{isAi ? 'Ask Grok about this chart. No @grok needed. Only you see this.' : 'No chat yet. @grok asks about the chart. The reply stays here.'}</div>
            ) : (
              chatList.map(m => (
                <div key={m.id} className="chat-row" onContextMenu={e => openReactEmoji(e, m)}>
                  <span className={'chat-name' + (m.name === 'Grok' ? ' grok' : '')}>{m.name}</span>
                  <span className="chat-text">
                    {renderChatText(isAi && m.name !== 'Grok' ? String(m.text || '').replace(/^@grok\b[:\s,]*/i, '') : m.text)}
                    {renderReactions(m)}
                  </span>
                  <span className="messages-row-time">
                    {fmtTime(m.ts)}
                  </span>
                </div>
              ))
            )}
          </div>
          {chat.error && <div className="chat-error">{chat.error}</div>}
          <div className="chat-input-row">
            {chat.identityReady && !chat.joined ? (
              <>
                <input
                  className="chat-input"
                  placeholder="pick a name"
                  value={chat.name}
                  maxLength={20}
                  onChange={e => chat.setName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') chat.join() }}
                />
                <button className="chat-send-btn" onClick={() => chat.join()}>Join</button>
              </>
            ) : chat.joined ? (
              <>
                <input
                  ref={chatInputRef}
                  className="chat-input"
                  placeholder={isAi ? 'ask grok about this chart' : "message…  (@grok for this chart)"}
                  value={chat.input}
                  maxLength={280}
                  onContextMenu={openInputEmoji}
                  onChange={e => chat.setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') sendNow() }}
                />
                <button
                  type="button"
                  className={'chat-mic-btn' + (micOn ? ' is-listening' : '')}
                  aria-pressed={micOn}
                  title={SpeechRecognitionAPI ? (micOn ? 'Stop' : 'Mic') : "speech isn't available"}
                  onClick={() => {
                    if (!SpeechRecognitionAPI) return
                    if (micOn) stopChatDictation()
                    else startChatDictation(chat.input, chat.setInput)
                  }}
                >Mic</button>
                <button className="chat-send-btn" onClick={sendNow}>Send</button>
              </>
            ) : null}
          </div>
          {emojiPop && (
            <EmojiPicker x={emojiPop.x} y={emojiPop.y} onPick={pickEmoji} onClose={closeEmoji} />
          )}
        </>
      )}
    </>
  )

  // Docked: fill the grid cell (no toggle, no popup positioning).
  if (docked) {
    return <div className="messages-panel messages-panel-docked">{panelInner}</div>
  }

  return (
    <>
      {showToggle && (
        <span style={{ display: 'inline-flex', alignItems: 'center', height: 21 }}>
          <button
            className="messages-toggle-btn"
            title="Messages — alerts, DEX paid, orders"
            onClick={() => setMessagesOpen(o => !o)}
          >
            {/* Rectangular message box with a tail */}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-4 4v-4H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
            </svg>
            {/* Two badges: red = log unread (base class), green = chat unread
                (.chat — offset left+down so both peek). Each clears only when
                the popup is opened on that tab (see effect). */}
            {messagesUnread > 0 && (
              <span className="messages-unread-badge">{messagesUnread > 99 ? '99+' : messagesUnread}</span>
            )}
            {chatUnread > 0 && (
              <span className="messages-unread-badge chat">{chatUnread > 99 ? '99+' : chatUnread}</span>
            )}
          </button>
        </span>
      )}

      {/* Messages popup — session scrollback of alerts / DEX / orders. */}
      {messagesOpen && (
        <div className="messages-panel">{panelInner}</div>
      )}
    </>
  )
}